import { getLocale } from "@utils/configuration/aem";

import { UnitRangeFacetValue } from "@components/Search/SearchResults/StateComponents/Facets/Facet/@types/facetType.types";
import {
    ConversionUnit,
    ConversionUnitType,
    convert,
    getPreferredUnitForValue,
    toUnitRangeUnit,
} from "@components/Search/SearchResults/StateComponents/Facets/Facet/components/UnitRangeFacet/unitRange.config";
import { UnitRangeFacetDisplayValue } from "@components/Search/SearchResults/StateComponents/Facets/Facet/components/UnitRangeFacet/unitRangeFacet.container";
import { RefinementSort } from "@components/Search/SearchResults/StateComponents/Facets/Facet/facetSortBy";

const formatAsLocaleNumber = (value: number) => {
    const roundedValue = Math.trunc(value * 10) / 10;

    const locale = getLocale(false).replace("_", "-");

    return roundedValue >= 10_000
        ? new Intl.NumberFormat(locale)
              .formatToParts(roundedValue)
              .map(part => (part.type === "group" ? " " : part.value))
              .join("")
        : new Intl.NumberFormat(locale, {
              useGrouping: false,
          }).format(roundedValue);
};

export const formatUnitRangeLabel = ({
    unit,
    lowerBound,
    upperBound,
}: UnitRangeFacetValue) => {
    const localeLowerBound = formatAsLocaleNumber(lowerBound);
    const localeUpperBound = formatAsLocaleNumber(upperBound);

    return `${localeLowerBound} - ${localeUpperBound} ${unit}`;
};

export const sortUnitRange = (
    a: UnitRangeFacetDisplayValue,
    b: UnitRangeFacetDisplayValue,
) => {
    const isRefinedSort = RefinementSort.IS_REFINED_DESC(a, b);
    if (isRefinedSort) return isRefinedSort;

    return a.lowerBoundMillimetres - b.lowerBoundMillimetres;
};

export const mapToFacetValueInMillimetres = (
    facetValue: UnitRangeFacetValue,
): UnitRangeFacetValue & {
    lowerBoundMillimetres: number;
    upperBoundMillimetres: number;
    initialUnit: ConversionUnitType;
} => {
    const unit = toUnitRangeUnit(facetValue.unit);

    return {
        ...facetValue,
        lowerBoundMillimetres: convert(
            facetValue.lowerBound,
            unit,
            ConversionUnit.Millimetre,
        ),
        upperBoundMillimetres: convert(
            facetValue.upperBound,
            unit,
            ConversionUnit.Millimetre,
        ),
        initialUnit: unit,
    };
};

export const mapToFacetValueWithDisplayValue = (
    facetValue: ReturnType<typeof mapToFacetValueInMillimetres>,
) => {
    const preferredUnit = getPreferredUnitForValue(
        facetValue.lowerBoundMillimetres,
        facetValue.initialUnit,
    );

    const displayValue = formatUnitRangeLabel({
        ...facetValue,
        lowerBound: convert(
            facetValue.lowerBoundMillimetres,
            ConversionUnit.Millimetre,
            preferredUnit,
        ),
        upperBound: convert(
            facetValue.upperBoundMillimetres,
            ConversionUnit.Millimetre,
            preferredUnit,
        ),
        unit:
            preferredUnit !== ConversionUnit.Default
                ? preferredUnit
                : facetValue.unit,
    });

    return {
        ...facetValue,
        displayValue: displayValue,
    };
};
